import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, PencilSimple, Trash, SignOut, House } from '@phosphor-icons/react';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const { token, logout, isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchProducts();
  }, [isAuthenticated, navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/admin');
      }
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await axios.delete(`${API}/admin/products/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(products.filter(p => p.id !== deleteId));
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete product');
    } finally {
      setDeleteId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-products-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black border-b border-border">
        <div className="px-6 md:px-12 py-4 flex items-center justify-between">
          <h1 
            className="text-2xl font-bold tracking-tighter uppercase"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            ADMIN PANEL
          </h1>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" data-testid="admin-home-btn">
                <House size={20} />
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <SignOut size={20} />
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 py-8">
        {/* Actions */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 
              className="text-3xl uppercase tracking-tight font-bold"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              PRODUCTS
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              {products.length} items
            </p>
          </div>
          <Link to="/admin/products/new">
            <Button 
              className="bg-white text-black hover:bg-primary hover:text-white"
              data-testid="add-product-btn"
            >
              <Plus size={18} className="mr-2" />
              ADD PRODUCT
            </Button>
          </Link>
        </div>

        {/* Products Table */}
        {products.length === 0 ? (
          <div className="text-center py-16 border border-border">
            <p className="text-muted-foreground mb-4">NO PRODUCTS YET</p>
            <Link to="/admin/products/new">
              <Button className="bg-white text-black hover:bg-primary hover:text-white">
                ADD YOUR FIRST PRODUCT
              </Button>
            </Link>
          </div>
        ) : (
          <div className="border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left text-xs uppercase tracking-wider p-4 font-bold">Product</th>
                  <th className="text-left text-xs uppercase tracking-wider p-4 font-bold hidden md:table-cell">Category</th>
                  <th className="text-left text-xs uppercase tracking-wider p-4 font-bold">Price</th>
                  <th className="text-left text-xs uppercase tracking-wider p-4 font-bold hidden sm:table-cell">Featured</th>
                  <th className="text-right text-xs uppercase tracking-wider p-4 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => (
                  <tr 
                    key={product.id} 
                    className="border-t border-border hover:bg-muted/50 transition-colors"
                    data-testid={`product-row-${index}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-16 bg-muted overflow-hidden flex-shrink-0">
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-sm truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
                            {product.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {product.sizes?.join(', ')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <span className="text-xs uppercase tracking-wide">
                        {product.subcategory}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="font-bold">{formatPrice(product.price)}</span>
                    </td>
                    <td className="p-4 hidden sm:table-cell">
                      <span className={`text-xs uppercase ${product.featured ? 'text-primary' : 'text-muted-foreground'}`}>
                        {product.featured ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/products/${product.id}/edit`}>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="hover:text-primary"
                            data-testid={`edit-product-${index}`}
                          >
                            <PencilSimple size={18} />
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="hover:text-destructive"
                          onClick={() => setDeleteId(product.id)}
                          data-testid={`delete-product-${index}`}
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-black border-border">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "'Oswald', sans-serif" }}>
              DELETE PRODUCT
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this product? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border hover:bg-muted">
              CANCEL
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              DELETE
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
